/**
 * How long an event lasts, split into the boxes a person fills in and added back up again.
 *
 * The date arithmetic lives in state.ts; this file is only the translation between one number of
 * minutes and the days/hours/minutes the fields show. Nobody should have to type the end time of
 * a 45-minute meeting they already know starts at 14:00.
 *
 * Splitting carries: 26 hours is one day and two hours, so a value typed past its unit's ceiling
 * settles into the field above it rather than being rejected or silently clamped.
 */

/**
 * A year, in minutes. Nothing this field is for lasts longer, and the cap is load-bearing rather
 * than tidy: the end is computed by adding minutes to a `Date`, and a fat-fingered "999999999"
 * would push it past the range a date can represent — which throws on the way to an ISO string
 * instead of showing a silly answer.
 */
export const MAX_DURATION_MINUTES = 365 * 1440;

export interface DurationParts {
  readonly days: number;
  readonly hours: number;
  readonly minutes: number;
}

/**
 * A duration as the three fields show it, with each unit inside its own range: 1560 minutes is
 * `{ days: 1, hours: 2, minutes: 0 }`, never `{ hours: 26 }`.
 */
export function splitDurationMinutes(total: number): DurationParts {
  const whole = Math.max(0, Math.min(Math.round(total), MAX_DURATION_MINUTES));
  return {
    days: Math.floor(whole / 1440),
    hours: Math.floor((whole % 1440) / 60),
    minutes: whole % 60,
  };
}

/**
 * What the fields hold, in minutes — `null` when they don't add up to a duration, which leaves
 * the event's end alone rather than guessing at one.
 *
 * Values arrive as strings because that is what both a DOM input and a React Native TextInput
 * hand back, and an empty box means zero rather than "unknown": clearing hours on a 90-minute
 * event makes it 30 minutes, not an error.
 */
export function durationPartsToMinutes(parts: {
  readonly days?: string | number;
  readonly hours?: string | number;
  readonly minutes?: string | number;
}): number | null {
  const days = fieldValue(parts.days);
  const hours = fieldValue(parts.hours);
  const minutes = fieldValue(parts.minutes);
  if (days === null || hours === null || minutes === null) return null;
  const total = days * 1440 + hours * 60 + minutes;
  if (total <= 0 || total > MAX_DURATION_MINUTES) return null;
  return total;
}

/** One box's contents: blank counts as zero, anything that isn't a whole number counts as junk. */
function fieldValue(value: string | number | undefined): number | null {
  if (value === undefined) return 0;
  if (typeof value === 'number') return Number.isInteger(value) && value >= 0 ? value : null;
  const text = value.trim();
  if (text === '') return 0;
  if (!/^\d+$/.test(text)) return null;
  const parsed = Number(text);
  return Number.isSafeInteger(parsed) ? parsed : null;
}
